import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-bootstrap/Modal";
import { ModalBody } from "react-bootstrap";
import { useListarElementos } from "../../Hooks/CRUDHooks";
import { GeocercaURL, GeocercaxPaisURL, GeocercaxPaisxTipoURL, sonidosVelocidadURL } from "../../API/apiurls";
import axios from "axios";
import ReactAudioPlayer from "react-audio-player";
import { BsGeoAltFill, BsMap, BsPinMapFill, BsSpeedometer2 } from "react-icons/bs";
import { AiFillSound } from "react-icons/ai";
import { ErrorMessage, Field, Form, Formik } from "formik";
import Select from "react-select";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import OSM from "ol/source/OSM";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import CircleGeom from "ol/geom/Circle";
import { Fill, Stroke, Style, Circle as CircleStyle } from "ol/style";
import { fromLonLat, toLonLat } from "ol/proj";
import "ol/ol.css";
import "../../Styles/CoordenadasModal.css";

const DEFAULT_COORDINATES = {
  lat: -12.046861,
  lon: -77.042341,
};

const MARKER_STYLE = new Style({
  image: new CircleStyle({
    radius: 8,
    fill: new Fill({ color: "#ffffff" }),
    stroke: new Stroke({ color: "#0d6efd", width: 4 }),
  }),
});

const CIRCLE_STYLE = new Style({
  fill: new Fill({ color: "rgba(13, 110, 253, 0.18)" }),
  stroke: new Stroke({ color: "rgba(13, 110, 253, 0.75)", width: 2 }),
});

function parseCoordinateValue(value) {
  if (!value) {
    return null;
  }

  const matches = String(value).match(/-?\d+(?:\.\d+)?/g);
  if (!matches || matches.length < 2) {
    return null;
  }

  const lat = Number.parseFloat(matches[0]);
  const lon = Number.parseFloat(matches[1]);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  return { lat, lon };
}

function formatCoordinateValue(lat, lon) {
  return `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
}

function normalizeProtocolCode(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/_/g, "-");
}

function isExtendedProtocol(protocolCode) {
  const code = normalizeProtocolCode(protocolCode);
  return code === "MT-3" || code === "MT-03" || code === "MT-3-JSON" || code === "MT-03-JSON";
}

function CoordinatePicker({ isOpen, coordinates, radius, onSelectCoordinates }) {
  const mapElementRef = useRef(null);
  const mapRef = useRef(null);
  const vectorSourceRef = useRef(null);
  const viewRef = useRef(null);
  const interactionRef = useRef(onSelectCoordinates);

  interactionRef.current = onSelectCoordinates;

  useEffect(() => {
    if (!isOpen || !mapElementRef.current || mapRef.current) {
      return undefined;
    }

    const vectorSource = new VectorSource();
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      zIndex: 10,
    });

    const initialPoint = parseCoordinateValue(coordinates) || DEFAULT_COORDINATES;
    const view = new View({
      center: fromLonLat([initialPoint.lon, initialPoint.lat]),
      zoom: parseCoordinateValue(coordinates) ? 16 : 12,
    });

    const map = new Map({
      target: mapElementRef.current,
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
        vectorLayer,
      ],
      view,
    });

    map.on("click", (event) => {
      const [lon, lat] = toLonLat(event.coordinate);
      interactionRef.current(formatCoordinateValue(lat, lon));
    });

    mapRef.current = map;
    vectorSourceRef.current = vectorSource;
    viewRef.current = view;

    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
      vectorSourceRef.current = null;
      viewRef.current = null;
    };
  }, [coordinates, isOpen]);

  useEffect(() => {
    if (!isOpen || !mapRef.current) {
      return;
    }

    setTimeout(() => {
      mapRef.current?.updateSize();
    }, 0);
  }, [isOpen]);

  useEffect(() => {
    if (!vectorSourceRef.current || !viewRef.current) {
      return;
    }

    const parsedCoordinates = parseCoordinateValue(coordinates);
    const vectorSource = vectorSourceRef.current;

    vectorSource.clear();

    if (!parsedCoordinates) {
      return;
    }

    const center = fromLonLat([parsedCoordinates.lon, parsedCoordinates.lat]);

    const radiusValue = Number.parseFloat(radius);
    if (Number.isFinite(radiusValue) && radiusValue > 0) {
      const circleFeature = new Feature({
        geometry: new CircleGeom(center, radiusValue),
      });
      circleFeature.setStyle(CIRCLE_STYLE);
      vectorSource.addFeature(circleFeature);
    }

    const markerFeature = new Feature({
      geometry: new Point(center),
    });
    markerFeature.setStyle(MARKER_STYLE);
    vectorSource.addFeature(markerFeature);

    if (Number.isFinite(radiusValue) && radiusValue > 0) {
      viewRef.current.fit(vectorSource.getExtent(), {
        padding: [48, 48, 48, 48],
        maxZoom: 17,
        duration: 250,
      });
      return;
    }

    viewRef.current.animate({
      center,
      zoom: 16,
      duration: 250,
    });
  }, [coordinates, radius]);

  const parsedCoordinates = parseCoordinateValue(coordinates);

  return (
    <div className="coordenadas-map-card">
      <div className="coordenadas-map-copy">
        <span className="coordenadas-map-kicker">
          <BsMap /> Ubicacion de la geocerca
        </span>
        <h5 className="coordenadas-map-title">Seleccione el punto en el mapa</h5>
        <p className="coordenadas-map-text">
          Haga clic sobre el mapa para cargar las coordenadas automaticamente. Si esta editando, el punto actual y su radio
          aparecen marcados.
        </p>
      </div>

      <div ref={mapElementRef} className="coordenadas-map-canvas" />

      <div className="coordenadas-map-footer">
        <div className="coordenadas-map-status">
          <BsPinMapFill />
          {parsedCoordinates ? (
            <span>{formatCoordinateValue(parsedCoordinates.lat, parsedCoordinates.lon)}</span>
          ) : (
            <span>Sin punto seleccionado todavia</span>
          )}
        </div>
        <span className="coordenadas-map-radius">
          Radio visual: {Number.isFinite(Number.parseFloat(radius)) && Number.parseFloat(radius) > 0 ? `${radius} m` : "Pendiente"}
        </span>
      </div>
    </div>
  );
}

function CoordenadasModal({ mostrar, cerrar, guardar, editar, datosaeditar, title, protocoloVista }) {
  const [selectedGeocerca, setSelectedGeocerca] = useState(null);
  const [geocercaOptions, setGeocercaOptions] = useState([]);
  const [geocercaTipo, setGeocercaTipo] = useState("Todas");
  const [velocidades, setVelocidades] = useState([]);
  const [audioSrc, setAudioSrc] = useState();
  const [audioGSrc, setAudioGSrc] = useState();
  const [imagenGsrc, setImagenGSrc] = useState();

  const pais = localStorage.getItem("pais");
  const resetFormRef = useRef(null);
  const showExtendedFields = isExtendedProtocol(protocoloVista);

  const initialValues = {
    id: datosaeditar ? datosaeditar.id : "",
    coordenadas: datosaeditar ? datosaeditar.coordenadas : "",
    radio: datosaeditar ? datosaeditar.radio : "",
    valorBoton: datosaeditar ? datosaeditar.valorBoton ?? 0 : 0,
    tiempoAudioVelocidad: datosaeditar ? datosaeditar.tiempoAudioVelocidad ?? 0 : 0,
    tiempoAudioGeocerca: datosaeditar ? datosaeditar.tiempoAudioGeocerca ?? 0 : 0,
    velocidad: datosaeditar ? datosaeditar.sonidosVelocidadModel.id : "",
    geocerca: datosaeditar ? datosaeditar.sonidosGeocercaModel.id : "",
    velocidadValor: datosaeditar ? datosaeditar.velocidadValor : "",
    sonidoVelocidad: datosaeditar ? datosaeditar.sonidoVelocidad : "",
    sonidoGeocerca: datosaeditar ? datosaeditar.sonidoGeocerca : "",
    imagenGeocerca: datosaeditar ? datosaeditar.imagenGeocerca : "",
    codsonidoG: datosaeditar ? datosaeditar.codsonidoG : "",
    codvel: datosaeditar ? datosaeditar.codvel : "",
  };

  useEffect(() => {
    if (!datosaeditar) {
      return;
    }

    setAudioSrc(datosaeditar.sonidoVelocidad);
    setAudioGSrc(datosaeditar.sonidosGeocercaModel.urlSonido);
    setImagenGSrc(datosaeditar.sonidosGeocercaModel.urlImagen);
    setSelectedGeocerca({
      label: `${datosaeditar.sonidosGeocercaModel.nombre} - ${datosaeditar.sonidosGeocercaModel.detalle}`,
      value: datosaeditar.sonidosGeocercaModel.id,
    });
  }, [datosaeditar]);

  useListarElementos(`${sonidosVelocidadURL}`, velocidades, setVelocidades);

  useEffect(() => {
    const fetchData = async () => {
      if (pais && geocercaTipo === "Todas") {
        try {
          const response = await axios.get(`${GeocercaxPaisURL}${pais}`);
          const options = response.data.map((geocerca) => ({
            label: `${geocerca.nombre} - ${geocerca.detalle}`,
            value: geocerca.id,
          }));
          setGeocercaOptions(options);
        } catch (error) {
          console.error("Error al cargar datos:", error, `${GeocercaxPaisURL}${pais}`);
        }
      } else if (pais && geocercaTipo !== "Todas") {
        try {
          let tc;
          if (geocercaTipo === "Reglamentarias") {
            tc = 1;
          } else if (geocercaTipo === "Preventivas") {
            tc = 2;
          } else if (geocercaTipo === "Informativas") {
            tc = 3;
          }
          const response = await axios.get(`${GeocercaxPaisxTipoURL}${pais}/${tc}`);
          const options = response.data.map((geocerca) => ({
            label: `${geocerca.nombre} - ${geocerca.detalle}`,
            value: geocerca.id,
          }));
          setGeocercaOptions(options);
        } catch (error) {
          console.error("Error al cargar datos:", error, `${GeocercaxPaisURL}${pais}`);
        }
      } else {
        const response = await axios.get(`${GeocercaURL}`);
        const options = response.data.map((geocerca) => ({
          label: `${geocerca.nombre} - ${geocerca.detalle}`,
          value: geocerca.id,
        }));
        setGeocercaOptions(options);
      }
    };

    fetchData();
  }, [pais, geocercaTipo]);

  const handleClose = () => {
    if (resetFormRef.current) {
      resetFormRef.current();
    }

    setSelectedGeocerca(null);
    setAudioSrc(null);
    setAudioGSrc(null);
    setImagenGSrc(null);
    cerrar();
  };

  const handleSave = (values) => {
    if (datosaeditar) {
      editar(values);
    } else {
      guardar(values);
    }

    setSelectedGeocerca(null);
    setAudioSrc(null);
    setAudioGSrc(null);
    setImagenGSrc(null);
    cerrar();
  };

  const openGoogleMaps = () => {
    const googleMapsUrl = "https://www.google.com/maps?q=-12.046861,-77.042341";
    window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
  };

  const geocercaSelectStyles = {
    control: (baseStyles, state) => ({
      ...baseStyles,
      minHeight: 42,
      borderRadius: 8,
      backgroundColor: "rgba(255, 255, 255, 0.96)",
      borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13, 110, 253, 0.15)" : "none",
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
      backgroundColor: "#ffffff",
      color: "#111827",
      zIndex: 30,
    }),
    option: (baseStyles, state) => ({
      ...baseStyles,
      backgroundColor: state.isFocused ? "#e9f2ff" : "#ffffff",
      color: "#111827",
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      color: "#111827",
    }),
    input: (baseStyles) => ({
      ...baseStyles,
      color: "#111827",
    }),
    placeholder: (baseStyles) => ({
      ...baseStyles,
      color: "#6b7280",
    }),
  };

  const audioDeVelocidad = async (event) => {
    const { value } = event.target;
    try {
      const response = await axios.get(`${sonidosVelocidadURL}/${value}`);
      setAudioSrc(response.data.sonidoVelocidad);
      return response.data.sonidoVelocidad;
    } catch (error) {
      console.error("Error al obtener los datos:", error);
      return null;
    }
  };

  const audioGeocerca = async (option) => {
    try {
      const response = await axios.get(`${GeocercaURL}/${option.value}`);
      setAudioGSrc(response.data.urlSonido);
      setImagenGSrc(response.data.urlImagen);
    } catch (error) {
      console.error("Error al obtener los datos:", error);
      return null;
    }
  };

  return (
    <Modal show={mostrar} onHide={handleClose} centered size="lg" dialogClassName="coordenadas-modal-shell">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values) => {
          await handleSave(values);
        }}
        enableReinitialize={true}
        innerRef={(instance) => {
          resetFormRef.current = instance?.resetForm || null;
        }}
      >
        {(formikProps) => (
          <Form>
            <ModalBody className="coordenadas-modal-body">
              <div className="coordenadas-modal-grid">
                <div className="coordenadas-modal-main">
                  <CoordinatePicker
                    isOpen={mostrar}
                    coordinates={formikProps.values.coordenadas}
                    radius={formikProps.values.radio}
                    onSelectCoordinates={(value) => formikProps.setFieldValue("coordenadas", value)}
                  />

                  <div className="coordenadas-fields-card">
                    <div className="coordenadas-field-header">
                      <span className="coordenadas-map-kicker">
                        <BsGeoAltFill /> Coordenadas manuales
                      </span>
                      <button type="button" className="coordenadas-link-button" onClick={openGoogleMaps}>
                        Abrir Google Maps
                      </button>
                    </div>

                    <div className="input-row">
                      <div className="input-column" style={{ width: "100%" }}>
                        <h5>Latitud y longitud</h5>
                        <Field type="text" name="coordenadas" className="inp2-form" placeholder="-12.046861, -77.042341" />
                        <p className="coordenadas-field-help">Formato esperado: latitud, longitud.</p>
                        <ErrorMessage name="coordenadas" component="div" className="error" />
                      </div>

                      <div className="input-column" style={{ width: "100%" }}>
                        <h5>Radio en metros</h5>
                        <Field type="text" name="radio" className="inp2-form" placeholder="100" />
                        <p className="coordenadas-field-help">El circulo sombreado del mapa usa este valor para mostrar el alcance.</p>
                        <ErrorMessage name="radio" component="div" className="error" />
                      </div>
                    </div>

                    {showExtendedFields && (
                      <>
                        <div className="input-row">
                          <div className="input-column" style={{ width: "100%" }}>
                            <h5>Tiempo audio velocidad</h5>
                            <Field
                              type="number"
                              name="tiempoAudioVelocidad"
                              className="inp2-form"
                              min="0"
                              placeholder="1200"
                            />
                            <p className="coordenadas-field-help">Tiempo asociado al audio de velocidad para MT-3 / MT-3-JSON.</p>
                            <ErrorMessage name="tiempoAudioVelocidad" component="div" className="error" />
                          </div>

                          <div className="input-column" style={{ width: "100%" }}>
                            <h5>Tiempo audio geocerca</h5>
                            <Field
                              type="number"
                              name="tiempoAudioGeocerca"
                              className="inp2-form"
                              min="0"
                              placeholder="900"
                            />
                            <p className="coordenadas-field-help">Tiempo asociado al audio de geocerca para MT-3 / MT-3-JSON.</p>
                            <ErrorMessage name="tiempoAudioGeocerca" component="div" className="error" />
                          </div>
                        </div>

                        <div className="input-row">
                          <div className="input-column" style={{ width: "100%" }}>
                            <h5>Valor botón</h5>
                            <Field
                              type="number"
                              name="valorBoton"
                              className="inp2-form"
                              min="0"
                              max="255"
                              placeholder="0"
                            />
                            <p className="coordenadas-field-help">Byte final del protocolo MT-3/MT-3-JSON. Rango de 0 a 255.</p>
                            <ErrorMessage name="valorBoton" component="div" className="error" />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="coordenadas-modal-side">
                  <div className="input-row">
                    <div className="input-column">
                      <h5>
                        <BsSpeedometer2 /> Velocidad
                      </h5>
                      <Field
                        as="select"
                        name="velocidad"
                        className="select-form"
                        onChange={(event) => {
                          formikProps.handleChange(event);
                          audioDeVelocidad(event);
                        }}
                      >
                        <option value="">Seleccione una velocidad</option>
                        {velocidades.map((velocidad) => (
                          <option key={velocidad.id} value={velocidad.id}>
                            {velocidad.nombre}
                          </option>
                        ))}
                      </Field>
                    </div>
                    <div className="input-column">
                      <AiFillSound style={{ marginRight: "10px" }} />
                      {formikProps.values.velocidad && typeof audioSrc === "string" && (
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <ReactAudioPlayer src={audioSrc} controls style={{ width: "min(100%, 220px)", marginTop: "15px" }} />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="input-row">
                    <div className="input-column">
                      <h5>
                        <BsSpeedometer2 /> Geocerca
                      </h5>
                      <Select
                        options={geocercaOptions}
                        value={selectedGeocerca}
                        name="geocerca"
                        styles={geocercaSelectStyles}
                        onChange={(selectedOption) => {
                          setSelectedGeocerca(selectedOption);
                          audioGeocerca(selectedOption);
                          formikProps.setFieldValue("geocerca", selectedOption.value);
                        }}
                        placeholder="Seleccione una geocerca"
                        isSearchable
                      />
                    </div>
                    <div className="input-column">
                      <div className="input-column">
                        <AiFillSound />
                        {selectedGeocerca && typeof audioGSrc === "string" && (
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <ReactAudioPlayer src={audioGSrc} controls style={{ width: "min(100%, 220px)", marginTop: "15px" }} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="input-row">
                    <div className="input-column">
                      <h5>Tipo de Geocerca</h5>
                      <div className="coordenadas-radio-grid">
                        <label className="rbstyle">
                          <input
                            type="radio"
                            name="geocercaTipo"
                            value="Todas"
                            checked={geocercaTipo === "Todas"}
                            onChange={() => setGeocercaTipo("Todas")}
                          />
                          Todas
                        </label>
                        <label className="rbstyle">
                          <input
                            type="radio"
                            name="geocercaTipo"
                            value="Reglamentarias"
                            checked={geocercaTipo === "Reglamentarias"}
                            onChange={() => setGeocercaTipo("Reglamentarias")}
                          />
                          Reglamentarias
                        </label>
                        <label className="rbstyle">
                          <input
                            type="radio"
                            name="geocercaTipo"
                            value="Preventivas"
                            checked={geocercaTipo === "Preventivas"}
                            onChange={() => setGeocercaTipo("Preventivas")}
                          />
                          Preventivas
                        </label>
                        <label className="rbstyle">
                          <input
                            type="radio"
                            name="geocercaTipo"
                            value="Informativas"
                            checked={geocercaTipo === "Informativas"}
                            onChange={() => setGeocercaTipo("Informativas")}
                          />
                          Informativas
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="coordenadas-preview-card">
                    <div className="input-column">
                      <h5>Vista previa de la geocerca</h5>
                      {selectedGeocerca && typeof imagenGsrc === "string" && (
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <img src={imagenGsrc} alt="Geocerca seleccionada" className="responsive-image-preview" />
                        </div>
                      )}
                    </div>
                  </div>

                  <ErrorMessage name="geocerca" component="div" className="error" />
                </div>
              </div>
            </ModalBody>
            <Modal.Footer>
              <button variant="primary" type="submit">
                Guardar
              </button>
              <button variant="secondary" type="button" onClick={handleClose}>
                Cerrar
              </button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

CoordinatePicker.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  coordinates: PropTypes.string,
  radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectCoordinates: PropTypes.func.isRequired,
};

CoordinatePicker.defaultProps = {
  coordinates: "",
  radius: "",
};

CoordenadasModal.propTypes = {
  mostrar: PropTypes.bool.isRequired,
  cerrar: PropTypes.func.isRequired,
  guardar: PropTypes.func.isRequired,
  editar: PropTypes.func.isRequired,
  datosaeditar: PropTypes.object,
  title: PropTypes.string,
  protocoloVista: PropTypes.string,
};

CoordenadasModal.defaultProps = {
  datosaeditar: null,
  title: "",
};

export default CoordenadasModal;
